import { useKeycloak } from 'all-in-one-react-keycloak';
import { Benefit, ClinicOrDoctorSearchResult, useClinicOrDoctorSearch } from 'all-in-one-shared-api';
import { Spin } from 'antd';
import { FormWrapper } from 'components/form';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimInfoCardUtils } from './utils';
import { useFormContext } from 'react-hook-form';
import { isLocaleEnglish } from 'utils/languageUtils';
import { getLocaleName } from 'utils/getLocaleDescription';
import { FormAutoComplete, FormAutoCompleteOption } from '../../../../components/form/FormAutoComplete';
import { findDoctorIdAndNameByQueryByOcr } from 'utils/claims';

interface DoctorSelectProps {
  selectedClaimTypeObject: Benefit[];
}

export const DoctorSelect = ({ selectedClaimTypeObject }: DoctorSelectProps) => {
  const { t, i18n } = useTranslation();
  const { initialized, keycloak } = useKeycloak();
  const { setValue, getValues, watch } = useFormContext();
  const doctorName: string = watch('doctorName');
  const doctorId: string = watch('doctorId');
  const doctorNameQuery: string = getValues('doctorNameQuery');
  const claimType = getValues('claimType');
  const claims = getValues('claims');
  const previousClaimTypes = useRef('');
  const doctorFromSelect = useRef<ClinicOrDoctorSearchResult>();
  const isChangeBySelect = useRef(false);
  const doctorDetails = getValues('doctorDetails');
  const specialties = useMemo(() => {
    if (!selectedClaimTypeObject) return '';

    return selectedClaimTypeObject.map((o) => o?.specialty?.split(',')).join(',');
  }, [selectedClaimTypeObject]);

  const toBenefitsString = (benefits: Benefit[]) => {
    return claimType === 'other' ? '' : (benefits || []).map((benefit: Benefit) => benefit.code).join(',');
  };

  const getClaimType = () => {
    // When claimType is other, it only has one benefit, and should be converted to claimType
    return claimType === 'other' ? toBenefitsString(claims) : claimType;
  };

  const {
    data: doctorData,
    shouldFetch,
    isLoading: doctorIsLoading,
  } = useClinicOrDoctorSearch(
    `doctorSearch?${specialties}&${doctorNameQuery}`,
    keycloak.token || '',
    doctorNameQuery,
    specialties,
    getClaimType(),
    toBenefitsString(claims),
    '0',
    '100',
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  );

  useEffect(() => {
    shouldFetch(initialized && !!keycloak.authenticated && !!keycloak.token);
  }, [initialized, keycloak.authenticated, keycloak.token, shouldFetch]);

  const isFreetextDoctor = useCallback((): boolean => {
    if (!doctorFromSelect.current) {
      return !!getValues('isFreetextDoctor');
    }
    return doctorFromSelect.current?.name !== doctorName && doctorFromSelect.current?.chineseName !== doctorName;
  }, [doctorName, getValues]);

  const currentClaimsString = useMemo(() => (claims || []).map((claim: Benefit) => claim.code).toString(), [claims]);
  const resetDoctor = useCallback(() => {
    setValue('doctorName', '');
    setValue('doctorId', '');
    setValue('doctorNameQuery', '');
  }, [setValue]);

  // const resetDoctorAfterChangeClaimType = useCallback(() => {
  //   if (!doctorData?.results) {
  //     return;
  //   }
  //   previousClaimTypes.current = currentClaimsString;
  //   const doctor = findDoctorIdAndNameByQueryByOcr(doctorData?.results || [], doctorName || doctorId);
  //   if (isFreetextDoctor()) return;

  //   if (!doctor.id) {
  //     resetDoctor();
  //   }
  // }, [doctorData?.results, currentClaimsString, doctorName, doctorId, isFreetextDoctor, resetDoctor]);

  useEffect(() => {
    if (!doctorData?.results) return;
    const doctor = findDoctorIdAndNameByQueryByOcr(doctorData?.results, doctorName, doctorId, doctorDetails);
    setValue('doctorId', doctor.id);
    setValue('doctorName', getLocaleName(isLocaleEnglish(i18n.language))(doctor));
    //eslint-disable-next-line
  }, [doctorData?.results, i18n.language, doctorId, doctorData]);

  useEffect(() => {
    // if (!currentClaimsString && !isFreetextDoctor()) {
    //   resetDoctor();
    // }
    if (!previousClaimTypes.current) {
      previousClaimTypes.current = currentClaimsString;
    }
    // if (previousClaimTypes.current !== currentClaimsString) {
    //   resetDoctorAfterChangeClaimType();
    // }
  }, [currentClaimsString, isFreetextDoctor, resetDoctor]);

  const handleOnDoctorSearch = (value: string) => {
    setValue('doctorDetails', { doctorCode: '', doctorName: value, doctorChineseName: value });
    setValue('doctorNameQuery', value);

    return doctorData;
  };

  const clearData = () => {
    doctorFromSelect.current = undefined;
    isChangeBySelect.current = false;
    setValue('doctorId', '');
    setValue('doctorName', '');
    setValue('doctorNameQuery', '');
  };

  const options = useMemo(() => {
    return doctorData
      ? ClaimInfoCardUtils.formatDoctorData(doctorData, i18n.language).map((doctor) => {
          return {
            value: doctor.id,
            label: <FormAutoCompleteOption name={doctor.name} caption={doctor.caption} />,
          };
        })
      : undefined;
  }, [doctorData, i18n.language]);

  return (
    <FormWrapper name="doctorName" label={t('newClaim.claimInfo.clinicOrDoctorLabel')}>
      <FormAutoComplete
        allowClear
        onSelect={(value: string) => {
          setValue('doctorId', value);
          doctorFromSelect.current = findDoctorIdAndNameByQueryByOcr(doctorData?.results || [], '', value);
          setValue('doctorName', doctorFromSelect.current.name);
          isChangeBySelect.current = true;
          setValue('isFreetextDoctor', false);
        }}
        name="doctorName"
        onChange={() => {
          if (!isChangeBySelect.current) {
            setValue('isFreetextDoctor', true);
            isChangeBySelect.current = false;
          }
        }}
        onClear={clearData}
        onSearch={handleOnDoctorSearch}
        showSearch
        placeholder={t('newClaim.claimInfo.clinicOrDoctorExtraText')}
        notFoundContent={doctorIsLoading ? <Spin size="small" /> : null}
        options={options}
        filterOption={false}
        disabled={currentClaimsString === ''}
      />
    </FormWrapper>
  );
};
